import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse, AxiosResponseHeaders } from "axios";
import { Thunk } from "../../store";
import {
  BackendCasosActivosPorContratoId,
  getCurrentDomain,
} from "../../../apis/backend/Api";
import mock_Json from "../../../dataJson/casosActivosPorContrato.json";
import {
  formatDescripcion,
  formatearMensajeBackend,
  ordenarCasosBackend,
  shouldRenderTicket,
} from "../../../apis/helper/helperApi";
import { TypeStatus } from "../../helpers/constantes";

interface State {
  listaCasosActivosPorContrato: any[];
  listaCasosActivosPorContratoAll: any[];
  status: TypeStatus;
  processDefinitionIdDisplayName: string;
  processDefinitionIdVersion: string;
  isLoading: boolean;
  Success: boolean;
  Mensaje: string | null;
  error: string | null;
}

const initialState: State = {
  listaCasosActivosPorContrato: [],
  listaCasosActivosPorContratoAll: [],
  processDefinitionIdDisplayName: "",
  processDefinitionIdVersion: "",
  status: "idle",
  isLoading: false,
  Success: false,
  Mensaje: "",
  error: null,
};
interface defaultResponse {
  data: any;
  status: number;
  statusText: string;
  headers: AxiosResponseHeaders | {};
  config: any;
  request?: any;
}
const defaultResp: defaultResponse = {
  data: undefined,
  status: 0,
  statusText: "",
  headers: {},
  config: undefined,
};
const listaCasosActivosPorContratoSlice = createSlice({
  name: "listaCasosActivosPorContrato",
  initialState,
  reducers: {
    setListaCasosActivosPorContrato: (state, action: PayloadAction<any[]>) => {
      state.listaCasosActivosPorContrato = action.payload;
    },
    setListaCasosActivosPorContratoAll: (
      state,
      action: PayloadAction<any[]>,
    ) => {
      state.listaCasosActivosPorContratoAll = action.payload;
    },
    getListaCasosActivosPorContrato: (state, action: PayloadAction<any[]>) => {
      state.listaCasosActivosPorContrato = action.payload;
    },
    setClearListaCasosActivosPorContrato: (
      state,
      action: PayloadAction<any[]>,
    ) => {
      state = initialState;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStatus: (state, action: PayloadAction<TypeStatus>) => {
      state.status = action.payload;
    },
    setResult: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      state.Success = action.payload;
    },
    setMensaje: (state, action: PayloadAction<string>) => {
      state.Mensaje = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setProcessDefinitionIdDisplayName: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.processDefinitionIdDisplayName = action.payload;
    },
    setProcessDefinitionIdVersion: (state, action: PayloadAction<string>) => {
      state.processDefinitionIdVersion = action.payload;
    },
  },
});
const procesarCasosActivosPorContrato = async (casos: any[]) => {
  return await Promise.all(
    casos.map(async (caso) => {
      const {
        alarma,
        descripcion,
        prioridad,
        ticketRedmine,
        ci,
        Usuario,
        NombreContrato,
      } = caso;
      let partesResumen = [];
      if (alarma) {
        partesResumen.push(`Titulo: ${alarma}`);
      }
      if (descripcion) {
        const value = formatDescripcion(descripcion);
        partesResumen.push(`Descripción: ${value}`);
      }
      if (ci) {
        partesResumen.push(`Ci: ${ci}`);
      }
      if (prioridad) {
        partesResumen.push(`Prioridad: ${prioridad}`);
      }
      if (Usuario) {
        partesResumen.push(`Usuario: ${Usuario}`);
      }
      if (NombreContrato) {
        partesResumen.push(`Contrato: ${NombreContrato}`);
      }

      const renderTicketRedmine = shouldRenderTicket(ticketRedmine);

      if (renderTicketRedmine) {
        partesResumen.push(`Ticket Redmine Nro: ${ticketRedmine}`);
      }
      const resumen =
        partesResumen.length > 0
          ? partesResumen.join("\n\n")
          : "No pudimos obtener los datos del caso";

      return {
        ...caso,
        resumen,
      };
    }),
  );
};
export const setBackendListaCasosActivosPorContrato =
  (
    IdCliente: number,
    IdContrato: number,
    TipoTicket: number, // 0 para Activos, 1 para inactivos
    IdCategoria: number,
    Cantidad: number, //2, // 0 para traer todos
    NumPagina: number,
  ): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));

    try {
      // Llamada inicial para obtener los casos activos por contrato
      const response: AxiosResponse = await BackendCasosActivosPorContratoId(
        IdCliente,
        IdContrato,
        TipoTicket, // 0 para Activos, 1 para inactivos
        IdCategoria,
        Cantidad, //2, // 0 para traer todos
        NumPagina,
      );

      const isError =
        response.data?.Success === false ||
        (response.data?.ResultadoSolicitud !== undefined &&
          response.data?.ResultadoSolicitud !== 200);

      if (isError) {
        dispatch(setIsLoading(false));
        dispatch(setStatus("failed"));
        dispatch(setSuccess(false));
      } else {
        let ordenarLista = [...(response.data.Data || [])];
        let listaFormateada =
          await procesarCasosActivosPorContrato(ordenarLista);
        dispatch(setListaCasosActivosPorContrato(listaFormateada));
        dispatch(setIsLoading(false));
        dispatch(setStatus("succeeded"));
        dispatch(setSuccess(true));

        if (!response.data) {
          response.data = { Data: [] };
        }
        response.data.Data = listaFormateada;
      }
      dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));

      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setSuccess(false));
      dispatch(setStatus("failed"));

      if (error.response?.status === 401) {
        dispatch(setStatus("noAutorice"));
        dispatch(
          setMensaje(
            formatearMensajeBackend(
              "Tu sesión ha expirado. Vuelve a iniciar sesión",
            ),
          ),
        );
      } else if (error.response) {
        // Hay respuesta del servidor, pero es otro error (400, 422, 500, etc.)
        console.error(
          "Error de servidor:",
          error.response.status,
          error.response.data,
        );

        const errorData = error.response.data;
        const mensajeBackend = errorData?.Mensaje;
        const resultadoSolicitud = errorData?.ResultadoSolicitud;

        if (
          mensajeBackend &&
          (resultadoSolicitud !== 200 || errorData?.Success === false)
        ) {
          dispatch(setMensaje(formatearMensajeBackend(mensajeBackend)));
        } else {
          dispatch(
            setMensaje(
              `Error ${error.response.status}: Ocurrió un problema en el servidor.`,
            ),
          );
        }
      } else if (error.request) {
        console.error("Error de conexión:", error.message);
        dispatch(
          setMensaje(
            "No se pudo conectar al servidor. Por favor, revisa tu conexión a internet o tu VPN.",
          ),
        );
      } else {
        console.error("Error de red o solicitud:", error.message);
        dispatch(
          setMensaje(
            "Ocurrió un problema con la red. Por favor, verifica tu conexión.",
          ),
        );
      }
      return error as AxiosError;
    } finally {
      dispatch(setIsLoading(false));
      if (getCurrentDomain() === "localhost") {
        dispatch(setIsLoading(true));
        let response: AxiosResponse = defaultResp;
        response.data = mock_Json;

        const mockData: any = mock_Json;
        const isErrorMock =
          mockData?.Success === false ||
          (mockData?.ResultadoSolicitud !== undefined &&
            mockData?.ResultadoSolicitud !== 200);

        if (isErrorMock) {
          dispatch(setIsLoading(false));
          dispatch(setStatus("failed"));
          dispatch(setSuccess(false));
        } else {
          let ordenarLista = [...(mock_Json.Data || [])];
          ordenarLista.sort(ordenarCasosBackend);
          let listaFormateada =
            await procesarCasosActivosPorContrato(ordenarLista);
          dispatch(setListaCasosActivosPorContrato(listaFormateada));
          dispatch(setIsLoading(false));
          dispatch(setStatus("succeeded"));
          dispatch(setSuccess(true));
        }
        dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));
        const mock401 = await import("../../../dataJson/401.json");
        if (mock401.status === 401) {
          dispatch(setStatus("noAutorice"));
          dispatch(
            setMensaje(
              formatearMensajeBackend(
                "Tu sesión ha expirado. Vuelve a iniciar sesión",
              ),
            ),
          );
        }
      }
    }
  };

export const setBackendClearListaCasosActivosPorContrato =
  (): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));
    try {
      let response: AxiosResponse = defaultResp;
      dispatch(setListaCasosActivosPorContrato([]));
      dispatch(setIsLoading(false));
      dispatch(setStatus("idle"));
      dispatch(setSuccess(false));
      dispatch(setMensaje(""));
      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setStatus("failed"));
      return error as AxiosError;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const {
  setListaCasosActivosPorContrato,
  setListaCasosActivosPorContratoAll,
  getListaCasosActivosPorContrato,
  setClearListaCasosActivosPorContrato,
  setIsLoading,
  setStatus,
  setResult,
  setSuccess,
  setMensaje,
  setError,
  setProcessDefinitionIdDisplayName,
  setProcessDefinitionIdVersion,
} = listaCasosActivosPorContratoSlice.actions;

export default listaCasosActivosPorContratoSlice.reducer;
